import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { allTenantDependentDataCleared } from '../../action/authenticationEvents';
import {
    isCreateBookingValid, isEditBookingValid, validatePersonalData, validateSomeoneElseData } from '../../utils';
import dayjs, { Dayjs } from 'dayjs';
import { stationPickerDay, stationTimeOfDay, zoneOfLocation } from '../../utils/stationTime';
import { PaymentStatusType } from '../../api/booking/bookingApiSlice';
import {
    Apartment,
    ApartmentDetails,
    AdditionalService as AssetAdditionalService,
    BookingCreationSelection,
    PersonalData,
    PersonalDataValidation,
    RentalBookingDetails,
    SomeoneElse,
    SomeoneElseData,
    SomeoneElseDataValidation,
} from '../../types/bookingCreation';

// What a booking-edit form holds for a stored check-in/out instant: the station
// DAY (for its date picker) and the station time (ADR-0019), on the clock of
// the booking's location (#3219). An absent value stays what `dayjs(null)`
// always made of it.
const editFormDay = (instant: Dayjs | string | null | undefined, timeZone: string): Dayjs =>
    instant ? stationPickerDay(instant, timeZone) : dayjs(instant);
const editFormTime = (instant: Dayjs | string | null | undefined, timeZone: string): string =>
    instant ? stationTimeOfDay(instant, timeZone) : '';

/**
 * The edit form's window fields for a stored booking — ONE definition for
 * loading the form and for "Revert changes". The API carries no separate
 * start/end time for most bookings, so the time falls back to the stored
 * instant's station time; without that fallback a reverted venue edit saved
 * midnight to midnight and the booking was re-priced at today's rates.
 * Days and times are read in the payload's `locationTimeZone`.
 */
const editFormWindow = (booking: RentalBookingDetails) => {
    const timeZone = zoneOfLocation(booking.locationTimeZone);
    return {
        checkIn: editFormDay(booking.checkIn, timeZone),
        checkOut: editFormDay(booking.checkOut, timeZone),
        startTime: booking.startTime || editFormTime(booking.checkIn, timeZone),
        endTime: booking.endTime || editFormTime(booking.checkOut, timeZone),
    };
};

export interface FirstNameChangedPayload {
    firstName: string;
}

export interface LastNameChangedPayload {
    lastName: string;
}

export interface EmailChangedPayload {
    email: string;
}

export interface AddressChangedPayload {
    address: string;
}

export interface CityChangedPayload {
    city: string;
}

export interface CountryChangedPayload {
    country: string;
}

export interface ZipCodeChangedPayload {
    zipCode: string;
}

export interface PhoneChangedPayload {
    phone: string;
}

export interface CompanyChangedPayload {
    company: string;
}

export interface CommentChangedPayload {
    comment: string;
}

export interface EmailNoteChangedPayload {
    emailNote: string;
}

export interface InvoiceNumberChangedPayload {
    invoiceNumber: string;
}

export interface CheckInDateSelectedPayload {
    checkInDate: Dayjs | null;
}

export interface CheckOutDateSelectedPayload {
    checkOutDate: Dayjs | null;
}

export interface StartTimeSelectedPayload {
    startTime?: string;
}

export interface EndTimeSelectedPayload {
    endTime?: string;
}

// export interface EventTypeSelectedPayload {
//     eventType: string;
// }

export interface CitySelectedPayload {
    city: string;
}

export interface NumberOfGuestsSelectedPayload {
    numberOfGuests: number;
}

export interface SomeoneElseFirstNameChangedPayload {
    firstName: string;
}

export interface SomeoneElseLastNameChangedPayload {
    lastName: string;
}

export interface SomeoneElseEmailChangedPayload {
    email: string;
}

export interface SomeoneElsePhoneChangedPayload {
    phone: string;
}

export interface SomeoneElseFirstNameChangedPayload {
    firstName: string;
}

export interface SomeoneElseLastNameChangedPayload {
    lastName: string;
}

export interface SomeoneElseEmailChangedPayload {
    email: string;
}

export interface SomeoneElsePhoneChangedPayload {
    phone: string;
}

export interface RemoveServicePayload {
    name: string;
}

export interface AdditionalService {
    serviceId: string;
    name: string;
    quantity: number; // -1 means no quantity for the service
    hasQuantity?: boolean;
    unitPrice?: number;
}
export interface BookingDetailsAdditionalService {
    id: string;
    serviceName: string;
    langNames?: Record<string, string>;
    unitPrice: number;
    quantity?: number; // -1 means no quantity for the service
    hasQuantity?: boolean;
    isChecked?: boolean;
}

export interface PersonalDataChangedPayload {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    address: string;
    city: string;
    zipCode: string;
    country: string;
}

export interface UpdatePaymentStatusPayload {
    bookingId: string;
    status: PaymentStatusType;
}

export interface RoomRentalLocation {
    city: string | null;
    district: string | null;
  }

export interface BookingCreationState {
    orderData: BookingCreationSelection;
    personalData: PersonalData;
    isForSomeoneElse: boolean;
    personalDataValidation: PersonalDataValidation;
    confirmEnabled: boolean;
    someoneElseDataValidation: SomeoneElseDataValidation;
    someoneElseData: SomeoneElseData;
    apartments: Apartment[] | null;
    selectedApartment: Apartment | null;
    selectedApartmentDetails: ApartmentDetails | null;
    selectedAdditionalServices: AdditionalService[];
    bookingDetails: RentalBookingDetails;
    editBookingDetails: RentalBookingDetails;
    selectedEditApartment: Apartment | null;
    selectedEditApartmentDetails: ApartmentDetails | null;
    // Selected add-ons that don't exist at the venue/apartment the admin
    // switched to during edit — kept for display (unchecked + notice) instead
    // of being silently wiped.
    editServicesUnavailableAtVenue: BookingDetailsAdditionalService[];
    paymentStatus: PaymentStatusType | null;
    roomRentalLocation: RoomRentalLocation;

}

const initialState: BookingCreationState = {
    orderData: {
        city: '',
        checkInDate: null,
        checkOutDate: null,
        startTime: '',
        endTime: '',
        eventType: null,
        amount: 1,
        numberOfGuests: 1,
        comment: '',
        emailNote: '',
    },
    personalData: {
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        address: '',
        city: '',
        zipCode: '',
        country: '',
        company: '',
    },
    isForSomeoneElse: false,
    personalDataValidation: {
        firstName: false,
        lastName: false,
        email: false,
        phone: false,
    },
    someoneElseDataValidation: {
        firstName: false,
        lastName: false,
        email: false,
    },
    someoneElseData: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
    },
    confirmEnabled: false,
    apartments: null,
    selectedApartment: null,
    selectedApartmentDetails: null,
    selectedAdditionalServices: [],
    bookingDetails: {
        id: '',
        // #2698: required on RentalBookingDetails, nullable on the wire. The
        // initial state is the "nothing loaded yet" shape, which is exactly a
        // null offer — not an empty string, which would be a groupId.
        groupId: null,
        useCase: '',
        invoiceNumber: '',
        bookingNumber: '',
        rentalEndDateTime: '',
        rentalStartDateTime: '',
        created_at: '',
        updated_at: '',
        status: 'booked',
        payment_status: 'unpaid',
        isMailSent: false,
        checkIn: null,
        checkOut: null,
        startTime: '',
        endTime: '',
        eventType: null,
        city: '',
        location: '',
        comment: '',
        address: {
            id: '',
            street: '',
            city: '',
            postalCode: '',
            geoLat: 0,
            geoLng: 0,
            archivedAt: null,
            createdAt: '',
            updatedAt: '',
        },
        numberOfGuests: 0,
        additionalService: [],
        allAdditionalServices: [],
        apartment: {
            id: '',
            name: '',
        },
        customerDetails: {
            // id: '',
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            city: '',
            zipCode: '',
            country: '',
            address: '',
            company: '',
        },
        guestDetails: {
            id: '',
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
        },
        payment: {
            nights: {
                nights: 0,
                price: 0,
            },
            compulsory: [],
            additionalServices: 0,
            couponApplied: 0,
            total: 0,
            amountPaid: 0,
            balance: 0,
            prices: [
                {
                    days: 'weekend',
                    price: 0,
                },
            ],
        },
    },
    editBookingDetails: {
        id: '',
        groupId: null,
        useCase: '',
        invoiceNumber: '',
        bookingNumber: '',
        created_at: '',
        updated_at: '',
        rentalEndDateTime: '',
        rentalStartDateTime: '',
        status: 'booked',
        payment_status: 'unpaid',
        isMailSent: false,
        checkIn: null,
        checkOut: null,
        startTime: '',
        endTime: '',
        eventType: null,
        city: '',
        location: '',
        comment: '',
        address: {
            id: '',
            street: '',
            city: '',
            postalCode: '',
            geoLat: 0,
            geoLng: 0,
            archivedAt: null,
            createdAt: '',
            updatedAt: '',
        },
        numberOfGuests: 0,
        additionalService: [],
        allAdditionalServices: [],
        apartment: {
            id: '',
            name: '',
        },
        customerDetails: {
            // id: '',
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            city: '',
            zipCode: '',
            country: '',
            address: '',
            company: '',
        },
        guestDetails: {
            id: '',
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
        },
        payment: {
            nights: {
                nights: 0,
                price: 0,
            },
            compulsory: [],
            additionalServices: 0,
            couponApplied: 0,
            total: 0,
            amountPaid: 0,
            balance: 0,
        },
    },
    selectedEditApartment: null,
    selectedEditApartmentDetails: null,
    editServicesUnavailableAtVenue: [],
    paymentStatus: null,
    roomRentalLocation: {
        city: null,
        district: null,
    },
};

const bookingCreationSlice = createSlice({
    name: 'bookingCreation',
    initialState,
    reducers: {
        setPersonalData: (
            state,
            action: PayloadAction<PersonalDataChangedPayload>,
        ) => {
            state.personalData = {
                ...state.personalData,
                ...action.payload,
            };
            state.personalDataValidation = validatePersonalData(
                state.personalData,
            );
        },
        setCheckInDate: (
            state,
            action: PayloadAction<CheckInDateSelectedPayload>,
        ) => {
            state.confirmEnabled = false;
            state.selectedApartment = null;
            state.orderData.checkInDate = action.payload.checkInDate;
            state.confirmEnabled = isCreateBookingValid({
                orderData: state.orderData,
                apartment: state.selectedApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setCheckOutDate: (
            state,
            action: PayloadAction<CheckOutDateSelectedPayload>,
        ) => {
            state.confirmEnabled = false;
            state.selectedApartment = null;
            state.orderData.checkOutDate = action.payload.checkOutDate;
            state.confirmEnabled = isCreateBookingValid({
                orderData: state.orderData,
                apartment: state.selectedApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setStartTime: (
            state,
            action: PayloadAction<StartTimeSelectedPayload>,
        ) => {
            state.orderData.startTime = action.payload.startTime || '';
            state.editBookingDetails.startTime = action.payload.startTime || '';
            state.confirmEnabled = isCreateBookingValid({
                orderData: state.orderData,
                apartment: state.selectedApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setEndTime: (
            state,
            action: PayloadAction<EndTimeSelectedPayload>,
        ) => {
            state.orderData.endTime = action.payload.endTime || '';
            state.editBookingDetails.endTime = action.payload.endTime || '';
            state.confirmEnabled = isCreateBookingValid({
                orderData: state.orderData,
                apartment: state.selectedApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setEventType: (
            state,
            action: PayloadAction<{mode: 'create' | 'edit', eventType: string | null}>,
        ) => {
            if (action.payload.mode === 'create') {
                state.orderData.eventType = action.payload.eventType;
                state.confirmEnabled = isCreateBookingValid({
                    orderData: state.orderData,
                    apartment: state.selectedApartment,
                    personalData: state.personalData,
                    someoneElseData: state.someoneElseData,
                    isForSomeoneElse: state.isForSomeoneElse,
                });
            }else {
                state.editBookingDetails.eventType = action.payload.eventType;
                state.confirmEnabled = isEditBookingValid({
                    orderData: {
                        city: state.editBookingDetails.city,
                        checkInDate: state.editBookingDetails.checkIn,
                        checkOutDate: state.editBookingDetails.checkOut,
                        startTime: state.editBookingDetails.startTime ?? '',
                        endTime: state.editBookingDetails.endTime ?? '',
                        eventType: state.editBookingDetails.eventType,
                        numberOfGuests: state.editBookingDetails.numberOfGuests,
                        amount: 1,
                        comment: state.editBookingDetails.comment,
                    },
                    apartment: state.selectedEditApartment,
                    personalData: state.personalData,
                    someoneElseData: state.someoneElseData,
                    isForSomeoneElse: state.isForSomeoneElse,
                });
            }
        },
        setCity: (state, action: PayloadAction<CitySelectedPayload>) => {
            state.confirmEnabled = false;
            state.selectedApartment = null;
            state.orderData.city = action.payload.city;
            state.confirmEnabled = isCreateBookingValid({
                orderData: state.orderData,
                apartment: state.selectedApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        clearCheckInDate: (state) => {
            state.orderData.checkInDate = null;
            state.confirmEnabled = false;
        },
        clearCheckOutDate: (state) => {
            state.orderData.checkOutDate = null;
            state.confirmEnabled = false;
        },
        setFirstName: (
            state,
            action: PayloadAction<FirstNameChangedPayload>,
        ) => {
            state.personalData.firstName = action.payload.firstName;
            state.personalDataValidation = validatePersonalData(
                state.personalData,
            );
            state.confirmEnabled = isCreateBookingValid({
                orderData: state.orderData,
                apartment: state.selectedApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setLastName: (state, action: PayloadAction<LastNameChangedPayload>) => {
            state.personalData.lastName = action.payload.lastName;
            state.personalDataValidation = validatePersonalData(
                state.personalData,
            );
            state.confirmEnabled = isCreateBookingValid({
                orderData: state.orderData,
                apartment: state.selectedApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setPhone: (state, action: PayloadAction<PhoneChangedPayload>) => {
            if (
                action.payload.phone !== '' &&
                !action.payload.phone.trim().startsWith('+')
            ) {
                state.personalData.phone = `+${action.payload.phone.trim()}`;
            } else {
                state.personalData.phone = action.payload.phone.trim();
            }
            state.personalDataValidation = validatePersonalData(
                state.personalData,
            );
            state.confirmEnabled = isCreateBookingValid({
                orderData: state.orderData,
                apartment: state.selectedApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setComment: (state, action: PayloadAction<CommentChangedPayload>) => {
            state.orderData.comment = action.payload.comment;
        },
        setEmailNote: (
            state,
            action: PayloadAction<EmailNoteChangedPayload>,
        ) => {
            state.orderData.emailNote = action.payload.emailNote;
        },
        setCompanyName: (state, action: PayloadAction<CompanyChangedPayload>) => {
            state.personalData.company = action.payload.company;
            state.personalDataValidation = validatePersonalData(
                state.personalData,
            );
            state.confirmEnabled = isCreateBookingValid({
                orderData: state.orderData,
                apartment: state.selectedApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setEmail: (state, action: PayloadAction<EmailChangedPayload>) => {
            state.personalData.email = action.payload.email;
            state.personalDataValidation = validatePersonalData(
                state.personalData,
            );
            state.confirmEnabled = isCreateBookingValid({
                orderData: state.orderData,
                apartment: state.selectedApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setAddress: (state, action: PayloadAction<AddressChangedPayload>) => {
            state.personalData.address = action.payload.address;
            state.personalDataValidation = validatePersonalData(
                state.personalData,
            );
            state.confirmEnabled = isCreateBookingValid({
                orderData: state.orderData,
                apartment: state.selectedApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setPersoCity: (state, action: PayloadAction<CityChangedPayload>) => {
            state.personalData.city = action.payload.city;
            state.personalDataValidation = validatePersonalData(
                state.personalData,
            );
            state.confirmEnabled = isCreateBookingValid({
                orderData: state.orderData,
                apartment: state.selectedApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setZipCode: (state, action: PayloadAction<ZipCodeChangedPayload>) => {
            state.personalData.zipCode = action.payload.zipCode;
            state.personalDataValidation = validatePersonalData(
                state.personalData,
            );
            state.confirmEnabled = isCreateBookingValid({
                orderData: state.orderData,
                apartment: state.selectedApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setCountry: (state, action: PayloadAction<CountryChangedPayload>) => {
            state.personalData.country = action.payload.country;
            state.personalDataValidation = validatePersonalData(
                state.personalData,
            );
            state.confirmEnabled = isCreateBookingValid({
                orderData: state.orderData,
                apartment: state.selectedApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setNumberOfGuests: (
            state,
            action: PayloadAction<NumberOfGuestsSelectedPayload>,
        ) => {
            state.selectedApartment = null;
            state.orderData.numberOfGuests = action.payload.numberOfGuests;
        },
        setForSomeoneElse: (state, action: PayloadAction<SomeoneElse>) => {
            state.isForSomeoneElse = action.payload.isForSomeoneElse;
            state.confirmEnabled = isCreateBookingValid({
                orderData: state.orderData,
                apartment: state.selectedApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setSomeoneElseFirstName: (
            state,
            action: PayloadAction<SomeoneElseFirstNameChangedPayload>,
        ) => {
            state.someoneElseData.firstName = action.payload.firstName;
            state.someoneElseDataValidation = validateSomeoneElseData(
                state.someoneElseData,
            );
            state.confirmEnabled = isCreateBookingValid({
                orderData: state.orderData,
                apartment: state.selectedApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setSomeoneElseLastName: (
            state,
            action: PayloadAction<SomeoneElseLastNameChangedPayload>,
        ) => {
            state.someoneElseData.lastName = action.payload.lastName;
            state.someoneElseDataValidation = validateSomeoneElseData(
                state.someoneElseData,
            );
            state.confirmEnabled = isCreateBookingValid({
                orderData: state.orderData,
                apartment: state.selectedApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setSomeoneElseEmail: (
            state,
            action: PayloadAction<SomeoneElseEmailChangedPayload>,
        ) => {
            state.someoneElseData.email = action.payload.email;
            state.someoneElseDataValidation = validateSomeoneElseData(
                state.someoneElseData,
            );
            state.confirmEnabled = isCreateBookingValid({
                orderData: state.orderData,
                apartment: state.selectedApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setSomeoneElsePhone: (
            state,
            action: PayloadAction<SomeoneElsePhoneChangedPayload>,
        ) => {
            if (
                action.payload.phone !== '' &&
                !action.payload.phone.trim().startsWith('+')
            ) {
                state.someoneElseData.phone = `+${action.payload.phone.trim()}`;
            } else {
                state.someoneElseData.phone = action.payload.phone.trim();
            }
            state.someoneElseDataValidation = validateSomeoneElseData(
                state.someoneElseData,
            );
            state.confirmEnabled = isCreateBookingValid({
                orderData: state.orderData,
                apartment: state.selectedApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setApartments: (state, action: PayloadAction<Apartment[]>) => {
            state.apartments = action.payload;
        },
        setSelectedApartment: (state, action: PayloadAction<Apartment>) => {
            state.selectedApartment = action.payload;
            state.confirmEnabled = isCreateBookingValid({
                orderData: state.orderData,
                apartment: state.selectedApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setSelectedApartmentDetails: (
            state,
            action: PayloadAction<ApartmentDetails | null>,
        ) => {
            state.selectedApartmentDetails = action.payload;
        },
        editService: (state, action: PayloadAction<AdditionalService>) => {
            const index = state.selectedAdditionalServices.findIndex(
                (service) => service.name === action.payload.name,
            );
            if (index !== -1) {
                state.selectedAdditionalServices[index] = {
                    ...state.selectedAdditionalServices[index],
                    ...action.payload,
                };
            }
        },
        addService: (state, action: PayloadAction<AdditionalService>) => {
            state.selectedAdditionalServices.push(action.payload);
        },
        removeService: (state, action: PayloadAction<RemoveServicePayload>) => {
            state.selectedAdditionalServices =
                state.selectedAdditionalServices.filter(
                    (item) => item.name !== action.payload.name,
                );
        },
        setBookingDetails: (
            state,
            action: PayloadAction<RentalBookingDetails>,
        ) => {
            state.bookingDetails = {
                ...action.payload,
                checkIn: dayjs(action.payload.checkIn),
                checkOut: dayjs(action.payload.checkOut),
                startTime: action.payload.startTime ? action.payload.startTime : '',
                endTime: action.payload.endTime ? action.payload.endTime : '',
            };
        },
        setEditBookingDetails: (
            state,
            action: PayloadAction<RentalBookingDetails>,
        ) => {
            // The edit form holds the picked DAY (what its date picker shows)
            // and the time separately, both on the station's clock (ADR-0019):
            // `dayjs(instant)` showed a 22.10. check-in as 21.10. to an
            // operator in London. The pages rebuild the instant with
            // `stationDateTime`.
            state.editBookingDetails = { ...action.payload, ...editFormWindow(action.payload) };
            state.editServicesUnavailableAtVenue = [];
            state.personalData = action.payload.customerDetails;
            state.personalDataValidation = validatePersonalData(
                state.personalData,
            );
            if (action.payload.guestDetails && Object.keys(action.payload.guestDetails).length > 0) {
                state.someoneElseData = action.payload.guestDetails;
                state.someoneElseDataValidation = validateSomeoneElseData(
                    state.someoneElseData,
                );
            }
            state.confirmEnabled = isEditBookingValid({
                orderData: {
                    city: state.editBookingDetails.city,
                    checkInDate: state.editBookingDetails.checkIn,
                    checkOutDate: state.editBookingDetails.checkOut,
                    startTime: state.editBookingDetails.startTime ?? '',
                    endTime: state.editBookingDetails.endTime ?? '',
                    eventType: state.editBookingDetails.eventType,
                    numberOfGuests: state.editBookingDetails.numberOfGuests,
                    amount: 1,
                    comment: state.editBookingDetails.comment,
                },
                apartment: state.selectedEditApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setRevertEditBookingReservation: (
            state,
            action: PayloadAction<RentalBookingDetails>,
        ) => {
            // Same fields as `setEditBookingDetails` (`editFormWindow`).
            state.editBookingDetails = { ...action.payload, ...editFormWindow(action.payload) };
            state.editServicesUnavailableAtVenue = [];
            state.confirmEnabled = isEditBookingValid({
                orderData: {
                    city: state.editBookingDetails.city,
                    checkInDate: state.editBookingDetails.checkIn,
                    checkOutDate: state.editBookingDetails.checkOut,
                    startTime: state.editBookingDetails.startTime ?? '',
                    endTime: state.editBookingDetails.endTime ?? '',
                    eventType: state.editBookingDetails.eventType,
                    numberOfGuests: state.editBookingDetails.numberOfGuests,
                    amount: 1,
                    comment: state.editBookingDetails.comment,
                },
                apartment: state.selectedEditApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setEditCheckInDate: (
            state,
            action: PayloadAction<CheckInDateSelectedPayload>,
        ) => {
            state.confirmEnabled = false;
            // state.selectedEditApartment = null;
            state.editBookingDetails.checkIn = action.payload.checkInDate;
            state.confirmEnabled = isEditBookingValid({
                orderData: {
                    city: state.editBookingDetails.city,
                    checkInDate: state.editBookingDetails.checkIn,
                    checkOutDate: state.editBookingDetails.checkOut,
                    startTime: state.editBookingDetails.startTime ?? '',
                    endTime: state.editBookingDetails.endTime ?? '',
                    eventType: state.editBookingDetails.eventType,
                    numberOfGuests: state.editBookingDetails.numberOfGuests,
                    amount: 1,
                    comment: state.editBookingDetails.comment,
                },
                apartment: state.selectedEditApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setEditCheckOutDate: (
            state,
            action: PayloadAction<CheckOutDateSelectedPayload>,
        ) => {
            state.confirmEnabled = false;
            // state.selectedEditApartment = null;
            state.editBookingDetails.checkOut = action.payload.checkOutDate;
            state.confirmEnabled = isEditBookingValid({
                orderData: {
                    city: state.editBookingDetails.city,
                    checkInDate: state.editBookingDetails.checkIn,
                    checkOutDate: state.editBookingDetails.checkOut,
                    startTime: state.editBookingDetails.startTime ?? '',
                    endTime: state.editBookingDetails.endTime ?? '',
                    eventType: state.editBookingDetails.eventType,
                    numberOfGuests: state.editBookingDetails.numberOfGuests,
                    amount: 1,
                    comment: state.editBookingDetails.comment,
                },
                apartment: state.selectedEditApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        clearEditCheckInDate: (state) => {
            state.editBookingDetails.checkIn = null;
            // state.confirmEnabled = false;
        },
        clearEditCheckOutDate: (state) => {
            state.editBookingDetails.checkOut = null;
            // state.confirmEnabled = false;
        },
        setEditNumberOfGuests: (
            state,
            action: PayloadAction<NumberOfGuestsSelectedPayload>,
        ) => {
            // state.selectedEditApartment = null;
            state.editBookingDetails.numberOfGuests = action.payload.numberOfGuests;
            state.confirmEnabled = isEditBookingValid({
                orderData: {
                    city: state.editBookingDetails.city,
                    checkInDate: state.editBookingDetails.checkIn,
                    checkOutDate: state.editBookingDetails.checkOut,
                    startTime: state.editBookingDetails.startTime ?? '',
                    endTime: state.editBookingDetails.endTime ?? '',
                    eventType: state.editBookingDetails.eventType,
                    numberOfGuests: state.editBookingDetails.numberOfGuests,
                    amount: 1,
                    comment: state.editBookingDetails.comment,
                },
                apartment: state.selectedEditApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setEditFirstName: (
            state,
            action: PayloadAction<FirstNameChangedPayload>,
        ) => {
            state.personalData.firstName = action.payload.firstName;
            state.personalDataValidation = validatePersonalData(
                state.personalData,
            );
            state.confirmEnabled = isEditBookingValid({
                orderData: {
                    city: state.editBookingDetails.city,
                    checkInDate: state.editBookingDetails.checkIn,
                    checkOutDate: state.editBookingDetails.checkOut,
                    startTime: state.editBookingDetails.startTime ?? '',
                    endTime: state.editBookingDetails.endTime ?? '',
                    eventType: state.editBookingDetails.eventType,
                    numberOfGuests: state.editBookingDetails.numberOfGuests,
                    amount: 1,
                    comment: state.editBookingDetails.comment,
                },
                apartment: state.selectedEditApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setEditLastName: (
            state,
            action: PayloadAction<LastNameChangedPayload>,
        ) => {
            state.personalData.lastName = action.payload.lastName;
            state.personalDataValidation = validatePersonalData(
                state.personalData,
            );
            state.confirmEnabled = isEditBookingValid({
                orderData: {
                    city: state.editBookingDetails.city,
                    checkInDate: state.editBookingDetails.checkIn,
                    checkOutDate: state.editBookingDetails.checkOut,
                    startTime: state.editBookingDetails.startTime ?? '',
                    endTime: state.editBookingDetails.endTime ?? '',
                    eventType: state.editBookingDetails.eventType,
                    numberOfGuests: state.editBookingDetails.numberOfGuests,
                    amount: 1,
                    comment: state.editBookingDetails.comment,
                },
                apartment: state.selectedEditApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setEditPhone: (state, action: PayloadAction<PhoneChangedPayload>) => {
            if (
                action.payload.phone !== '' &&
                !action.payload.phone.trim().startsWith('+')
            ) {
                state.personalData.phone = `+${action.payload.phone.trim()}`;
            } else {
                state.personalData.phone = action.payload.phone.trim();
            }
            state.personalDataValidation = validatePersonalData(
                state.personalData,
            );
            state.confirmEnabled = isEditBookingValid({
                orderData: {
                    city: state.editBookingDetails.city,
                    checkInDate: state.editBookingDetails.checkIn,
                    checkOutDate: state.editBookingDetails.checkOut,
                    startTime: state.editBookingDetails.startTime ?? '',
                    endTime: state.editBookingDetails.endTime ?? '',
                    eventType: state.editBookingDetails.eventType,
                    numberOfGuests: state.editBookingDetails.numberOfGuests,
                    amount: 1,
                    comment: state.editBookingDetails.comment,
                },
                apartment: state.selectedEditApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setEditEmail: (state, action: PayloadAction<EmailChangedPayload>) => {
            state.personalData.email = action.payload.email;
            state.personalDataValidation = validatePersonalData(
                state.personalData,
            );
            state.confirmEnabled = isEditBookingValid({
                orderData: {
                    city: state.editBookingDetails.city,
                    checkInDate: state.editBookingDetails.checkIn,
                    checkOutDate: state.editBookingDetails.checkOut,
                    startTime: state.editBookingDetails.startTime ?? '',
                    endTime: state.editBookingDetails.endTime ?? '',
                    eventType: state.editBookingDetails.eventType,
                    numberOfGuests: state.editBookingDetails.numberOfGuests,
                    amount: 1,
                    comment: state.editBookingDetails.comment,
                },
                apartment: state.selectedEditApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setEditCompany: (state, action: PayloadAction<CompanyChangedPayload>) => {
            state.personalData.company = action.payload.company;
        },
        setEditComment: (state, action: PayloadAction<CommentChangedPayload>) => {
            state.editBookingDetails.comment = action.payload.comment;
        },
        setEditEmailNote: (
            state,
            action: PayloadAction<EmailNoteChangedPayload>,
        ) => {
            state.editBookingDetails.emailNote = action.payload.emailNote;
        },
        setEditInvoiceNumber: (state, action: PayloadAction<InvoiceNumberChangedPayload>) => {
            state.editBookingDetails.invoiceNumber = action.payload.invoiceNumber;
        },
        setEditAddress: (state, action: PayloadAction<AddressChangedPayload>) => {
            state.personalData.address = action.payload.address;
            state.personalDataValidation = validatePersonalData(
                state.personalData,
            );
            state.confirmEnabled = isEditBookingValid({
                orderData: {
                    city: state.editBookingDetails.city,
                    checkInDate: state.editBookingDetails.checkIn,
                    checkOutDate: state.editBookingDetails.checkOut,
                    startTime: state.editBookingDetails.startTime ?? '',
                    endTime: state.editBookingDetails.endTime ?? '',
                    eventType: state.editBookingDetails.eventType,
                    numberOfGuests: state.editBookingDetails.numberOfGuests,
                    amount: 1,
                    comment: state.editBookingDetails.comment,
                },
                apartment: state.selectedEditApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setEditPersoCity: (state, action: PayloadAction<CityChangedPayload>) => {
            state.personalData.city = action.payload.city;
            state.personalDataValidation = validatePersonalData(
                state.personalData,
            );
            state.confirmEnabled = isEditBookingValid({
                orderData: {
                    city: state.editBookingDetails.city,
                    checkInDate: state.editBookingDetails.checkIn,
                    checkOutDate: state.editBookingDetails.checkOut,
                    startTime: state.editBookingDetails.startTime ?? '',
                    endTime: state.editBookingDetails.endTime ?? '',
                    eventType: state.editBookingDetails.eventType,
                    numberOfGuests: state.editBookingDetails.numberOfGuests,
                    amount: 1,
                    comment: state.editBookingDetails.comment,
                },
                apartment: state.selectedEditApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setEditZipCode: (state, action: PayloadAction<ZipCodeChangedPayload>) => {
            state.personalData.zipCode = action.payload.zipCode;
            state.personalDataValidation = validatePersonalData(
                state.personalData,
            );
            state.confirmEnabled = isEditBookingValid({
                orderData: {
                    city: state.editBookingDetails.city,
                    checkInDate: state.editBookingDetails.checkIn,
                    checkOutDate: state.editBookingDetails.checkOut,
                    startTime: state.editBookingDetails.startTime ?? '',
                    endTime: state.editBookingDetails.endTime ?? '',
                    eventType: state.editBookingDetails.eventType,
                    numberOfGuests: state.editBookingDetails.numberOfGuests,
                    amount: 1,
                    comment: state.editBookingDetails.comment,
                },
                apartment: state.selectedEditApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setEditCountry: (state, action: PayloadAction<CountryChangedPayload>) => {
            state.personalData.country = action.payload.country.trim();
            state.personalDataValidation = validatePersonalData(
                state.personalData,
            );
            state.confirmEnabled = isEditBookingValid({
                orderData: {
                    city: state.editBookingDetails.city,
                    checkInDate: state.editBookingDetails.checkIn,
                    checkOutDate: state.editBookingDetails.checkOut,
                    startTime: state.editBookingDetails.startTime ?? '',
                    endTime: state.editBookingDetails.endTime ?? '',
                    eventType: state.editBookingDetails.eventType,
                    numberOfGuests: state.editBookingDetails.numberOfGuests,
                    amount: 1,
                    comment: state.editBookingDetails.comment,
                },
                apartment: state.selectedEditApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        editEditService: (state, action: PayloadAction<AdditionalService>) => {
            const index = state.editBookingDetails.additionalService?.findIndex(
                (service) => service.serviceName === action.payload.name,
            );
            if (index !== -1) {
                state.editBookingDetails.additionalService[index] = {
                    ...state.editBookingDetails.additionalService[index],
                    ...action.payload,
                };
            }
        },
        addEditService: (state, action: PayloadAction<AdditionalService>) => {
            state.editBookingDetails.additionalService.push({
                id: action.payload.serviceId,
                serviceName: action.payload.name,
                quantity: action.payload.quantity,
                hasQuantity: action.payload.hasQuantity,
                unitPrice: action.payload.unitPrice ?? 0,
                isChecked: true,

            });
        },
        removeEditService: (state, action: PayloadAction<RemoveServicePayload>) => {
            state.editBookingDetails.additionalService =
                state.editBookingDetails.additionalService.filter(
                    (item) => item.serviceName !== action.payload.name,
                );
        },
        clearEditAdditionalServices: (state) => {
            state.editBookingDetails.additionalService = [];
            state.editServicesUnavailableAtVenue = [];
        },
        // Venue/apartment switch during edit: service rows are per asset
        // (asset_additional_services.asset_id), so the selection must be
        // re-matched by service name against the new asset's catalog. Matches
        // stay selected with the new asset's row (id/price/quantity flag);
        // the rest moves to editServicesUnavailableAtVenue so the UI can show
        // them unchecked with a notice instead of silently dropping them.
        remapEditServicesToVenueCatalog: (
            state,
            action: PayloadAction<{ catalog: AssetAdditionalService[] }>,
        ) => {
            const catalog = action.payload.catalog;
            const findByName = (name: string) =>
                catalog.find(
                    (row) => row.serviceName.trim().toLowerCase() === name.trim().toLowerCase(),
                );
            const kept: BookingDetailsAdditionalService[] = [];
            const unavailable: BookingDetailsAdditionalService[] = [];
            for (const selected of state.editBookingDetails.additionalService) {
                const match = findByName(selected.serviceName);
                if (match) {
                    kept.push({
                        ...selected,
                        id: match.id,
                        unitPrice: match.unitPrice,
                        langNames: match.langNames,
                        hasQuantity: match.quantity,
                        quantity: match.quantity ? selected.quantity : undefined,
                    });
                } else {
                    unavailable.push(selected);
                }
            }
            // Previously unavailable add-ons that the new asset offers again
            // drop out of the notice (they reappear in the catalog, unchecked —
            // deliberately not re-selected); still-unavailable ones stay listed.
            for (const prev of state.editServicesUnavailableAtVenue) {
                if (
                    !findByName(prev.serviceName) &&
                    !unavailable.some((s) => s.serviceName === prev.serviceName)
                ) {
                    unavailable.push(prev);
                }
            }
            state.editBookingDetails.additionalService = kept;
            state.editServicesUnavailableAtVenue = unavailable;
        },
        setSomeoneElseEditFirstName: (
            state,
            action: PayloadAction<SomeoneElseFirstNameChangedPayload>,
        ) => {
            state.someoneElseData.firstName = action.payload.firstName;
            state.someoneElseDataValidation = validateSomeoneElseData(
                state.someoneElseData,
            );
            state.confirmEnabled = isEditBookingValid({
                orderData: {
                    city: state.editBookingDetails.city,
                    checkInDate: state.editBookingDetails.checkIn,
                    checkOutDate: state.editBookingDetails.checkOut,
                    startTime: state.editBookingDetails.startTime ?? '',
                    endTime: state.editBookingDetails.endTime ?? '',
                    eventType: state.editBookingDetails.eventType,
                    numberOfGuests: state.editBookingDetails.numberOfGuests,
                    amount: 1,
                    comment: state.editBookingDetails.comment,
                },
                apartment: state.selectedEditApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setSomeoneElseEditLastName: (
            state,
            action: PayloadAction<SomeoneElseLastNameChangedPayload>,
        ) => {
            state.someoneElseData.lastName = action.payload.lastName;
            state.someoneElseDataValidation = validateSomeoneElseData(
                state.someoneElseData,
            );
            state.confirmEnabled = isEditBookingValid({
                orderData: {
                    city: state.editBookingDetails.city,
                    checkInDate: state.editBookingDetails.checkIn,
                    checkOutDate: state.editBookingDetails.checkOut,
                    startTime: state.editBookingDetails.startTime ?? '',
                    endTime: state.editBookingDetails.endTime ?? '',
                    eventType: state.editBookingDetails.eventType,
                    numberOfGuests: state.editBookingDetails.numberOfGuests,
                    amount: 1,
                    comment: state.editBookingDetails.comment,
                },
                apartment: state.selectedEditApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setSomeoneElseEditEmail: (
            state,
            action: PayloadAction<SomeoneElseEmailChangedPayload>,
        ) => {
            state.someoneElseData.email = action.payload.email;
            state.someoneElseDataValidation = validateSomeoneElseData(
                state.someoneElseData,
            );
            state.confirmEnabled = isEditBookingValid({
                orderData: {
                    city: state.editBookingDetails.city,
                    checkInDate: state.editBookingDetails.checkIn,
                    checkOutDate: state.editBookingDetails.checkOut,
                    startTime: state.editBookingDetails.startTime ?? '',
                    endTime: state.editBookingDetails.endTime ?? '',
                    eventType: state.editBookingDetails.eventType,
                    numberOfGuests: state.editBookingDetails.numberOfGuests,
                    amount: 1,
                    comment: state.editBookingDetails.comment,
                },
                apartment: state.selectedEditApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setSomeoneElseEditPhone: (
            state,
            action: PayloadAction<SomeoneElsePhoneChangedPayload>,
        ) => {
            if (
                action.payload.phone !== '' &&
                !action.payload.phone.trim().startsWith('+')
            ) {
                state.someoneElseData.phone = `+${action.payload.phone.trim()}`;
            } else {
                state.someoneElseData.phone = action.payload.phone.trim();
            }
            state.someoneElseDataValidation = validateSomeoneElseData(
                state.someoneElseData,
            );
            state.confirmEnabled = isEditBookingValid({
                orderData: {
                    city: state.editBookingDetails.city,
                    checkInDate: state.editBookingDetails.checkIn,
                    checkOutDate: state.editBookingDetails.checkOut,
                    startTime: state.editBookingDetails.startTime ?? '',
                    endTime: state.editBookingDetails.endTime ?? '',
                    eventType: state.editBookingDetails.eventType,
                    numberOfGuests: state.editBookingDetails.numberOfGuests,
                    amount: 1,
                    comment: state.editBookingDetails.comment,
                },
                apartment: state.selectedEditApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        updatePaymentStatus: (
            state,
            action: PayloadAction<UpdatePaymentStatusPayload>,
        ) => {
            state.paymentStatus = action.payload.status;
            if (state.bookingDetails) {
                state.bookingDetails.payment_status = action.payload.status;
            }
        },
        clearBookingCreationStates: (state) => {
            Object.assign(state, initialState);
        },
        setSelectedEditApartment: (state, action: PayloadAction<Apartment>) => {
            state.selectedEditApartment = action.payload;

            state.confirmEnabled = isEditBookingValid({
                orderData: {
                    city: state.editBookingDetails.city,
                    checkInDate: state.editBookingDetails.checkIn,
                    checkOutDate: state.editBookingDetails.checkOut,
                    startTime: state.editBookingDetails.startTime ?? '',
                    endTime: state.editBookingDetails.endTime ?? '',
                    eventType: state.editBookingDetails.eventType,
                    numberOfGuests: state.editBookingDetails.numberOfGuests,
                    amount: 1,
                    comment: state.editBookingDetails.comment,
                },
                apartment: state.selectedEditApartment,
                personalData: state.personalData,
                someoneElseData: state.someoneElseData,
                isForSomeoneElse: state.isForSomeoneElse,
            });
        },
        setSelectedEditApartmentDetails: (
            state,
            action: PayloadAction<ApartmentDetails | null>,
        ) => {
            state.selectedEditApartmentDetails = action.payload;
        },
        setRoomRentalLocation: (
            state,
            action: PayloadAction<RoomRentalLocation>,
        ) => {
            state.roomRentalLocation = action.payload;
            // state.nextEnabled = isSearchRoomDataValid({
            //     city: state.roomRentalLocation.city !== null,
            //     district: state.roomRentalLocation.district !== null,
            //     checkInDate: state.orderData.checkInDate !== null,
            //     checkOutDate: state.orderData.checkOutDate !== null,
            //     numberOfGuests: true,
            // });
        },
    },
    // Wipe any in-flight Create-Booking form state when the tenant changes,
    // so a partially-filled draft from tenant A (selected location, dates,
    // customer info) can't bleed into tenant B's form.
    extraReducers: (builder) => {
        builder.addCase(allTenantDependentDataCleared.toString(), () => initialState);
    },
});


export const {
    setBookingDetails,
    updatePaymentStatus,
    setPersonalData,
    setCheckInDate,
    setCheckOutDate,
    setStartTime,
    setEndTime,
    setEventType,
    setCity,
    clearCheckInDate,
    clearCheckOutDate,
    setFirstName,
    setLastName,
    setPhone,
    setEmail,
    setNumberOfGuests,
    setAddress,
    setCountry,
    setPersoCity,
    setZipCode,
    setForSomeoneElse,
    setSomeoneElseFirstName,
    setSomeoneElseLastName,
    setSomeoneElseEmail,
    setSomeoneElsePhone,
    setApartments,
    setSelectedApartment,
    setSelectedApartmentDetails,
    editService,
    addService,
    removeService,
    setEditBookingDetails,
    setRevertEditBookingReservation,
    clearBookingCreationStates,
    setEditCheckInDate,
    setEditCheckOutDate,
    clearEditCheckInDate,
    clearEditCheckOutDate,
    setEditNumberOfGuests,
    editEditService,
    removeEditService,
    addEditService,
    clearEditAdditionalServices,
    remapEditServicesToVenueCatalog,
    setEditFirstName,
    setEditLastName,
    setEditPhone,
    setEditEmail,
    setCompanyName,
    setComment,
    setEmailNote,
    setEditComment,
    setEditEmailNote,
    setEditInvoiceNumber,
    setEditAddress,
    setEditCompany,
    setEditCountry,
    setEditPersoCity,
    setEditZipCode,
    setSomeoneElseEditEmail,
    setSomeoneElseEditFirstName,
    setSomeoneElseEditLastName,
    setSomeoneElseEditPhone,
    setSelectedEditApartment,
    setSelectedEditApartmentDetails,
    setRoomRentalLocation,
} = bookingCreationSlice.actions;

export { bookingCreationSlice };
